/**
 * Experiment status chip — colour-coded `<Chip>` shared between the list
 * table, the mobile card-list view, and the detail page header (#25).
 *
 * Colour mapping (issue #25 hard constraint):
 *   - DRAFT     → default (grey)
 *   - RUNNING   → success (green)
 *   - PAUSED    → warning (amber)
 *   - COMPLETED → info (cyan)
 *   - ARCHIVED  → default (grey, outlined to disambiguate from DRAFT)
 */

import type { ReactElement } from "react";
import { Chip } from "@mui/material";
import type { ChipProps } from "@mui/material";
import type { ExperimentStatus } from "@ottopilot/shared";

const STATUS_COLOR: Record<ExperimentStatus, ChipProps["color"]> = {
  DRAFT: "default",
  RUNNING: "success",
  PAUSED: "warning",
  COMPLETED: "info",
  ARCHIVED: "default",
};

export type ExperimentStatusChipProps = {
  status: ExperimentStatus;
  size?: ChipProps["size"];
};

export function ExperimentStatusChip({
  status,
  size = "small",
}: ExperimentStatusChipProps): ReactElement {
  return (
    <Chip
      size={size}
      label={status}
      color={STATUS_COLOR[status]}
      variant={status === "ARCHIVED" ? "outlined" : "filled"}
      data-testid={`experiment-status-chip-${status}`}
    />
  );
}
